import { REAGION_SOATO } from '@/common/constants/regions';
import { ICottonAnalyticalResponse } from '../models/ICottonAnalytical';

const HEADER_BG = 'FFF3F4F6';
const TOTAL_BG = 'FFF3F4F6';
const ROW_ODD_BG = 'FFF9FAFB';
const ROW_EVEN_BG = 'FFFFFFFF';
const BORDER_COLOR = 'FFD1D5DB';

const border = () => ({
    top: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    left: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    bottom: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    right: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
});

export async function exportCottonAnalyticalToExcel(
    data: ICottonAnalyticalResponse,
    filename = 'paxta-tahliliy-jadvali',
) {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Paxta tahliliy jadvali');

    // Barcha viloyatlardan noyob action larni yig'ib, order bo'yicha tartiblab olamiz
    const columns = (() => {
        const map = new Map<string, { id: string; name: string; order: number }>();
        for (const region of data.data) {
            for (const action of region.actions_data) {
                if (!map.has(action.action_id)) {
                    map.set(action.action_id, { id: action.action_id, name: action.action_name, order: action.order });
                }
            }
        }
        return Array.from(map.values()).sort((a, b) => a.order - b.order);
    })();

    const regions = REAGION_SOATO
        .map((r) => data.data.find((d) => d.region_id === r.id))
        .filter(Boolean) as typeof data.data;

    ws.columns = [
        { width: 6 },
        { width: 28 },
        { width: 20 },
        ...columns.map(() => ({ width: 20 })),
    ];

    const totalCols = 3 + columns.length;

    // ─── HEADER ROW ────────────────────────────────────────────────────────────
    const headerValues = ['№', 'Hudud nomi', 'Monitoring qilinadigan umumiy yer rejasi (ga)', ...columns.map((a) => a.name)];
    const hRow = ws.addRow(headerValues);
    hRow.height = 52;
    hRow.eachCell((cell) => {
        cell.font = { bold: true, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
        cell.border = border();
    });

    // ─── DATA ROWS ─────────────────────────────────────────────────────────────
    regions.forEach((region, idx) => {
        const rowBg = idx % 2 === 0 ? ROW_EVEN_BG : ROW_ODD_BG;
        const soato = REAGION_SOATO.find((r) => r.id === region.region_id)?.soato;
        const planData = data.plans?.find((p) => p.region_soato === soato);

        const values: (string | number)[] = [
            idx + 1,
            region.region_name,
            planData ? Number(planData.all_projected_area) : '',
            ...columns.map((col) => {
                const found = region.actions_data.find((a) => a.action_id === col.id);
                return found ? found.inspected_area : '';
            }),
        ];
        const row = ws.addRow(values);
        row.height = 22;
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
            cell.border = border();
            if (colNumber === 1) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.font = { size: 10, color: { argb: 'FF6B7280' } };
            } else if (colNumber === 2) {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
                cell.font = { bold: true, size: 10 };
            } else {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.font = { size: 10, color: { argb: 'FF1E40AF' } };
                cell.numFmt = '#,##0.0';
            }
        });
    });

    // ─── JAMI ROW ──────────────────────────────────────────────────────────────
    const totalsValues: (string | number)[] = [
        '',
        'Jami',
        data.plans?.reduce((acc, p) => acc + Number(p.all_projected_area), 0) || '',
        ...columns.map((col) => {
            const t = data.totals.find((tot) => tot.action_id === col.id);
            return t ? t.total : '';
        }),
    ];
    const totalRow = ws.addRow(totalsValues);
    totalRow.height = 24;
    totalRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTAL_BG } };
        cell.border = border();
        if (colNumber === 2) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
            cell.font = { bold: true, size: 10 };
        } else {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.font = { bold: true, size: 10, color: { argb: 'FF1E40AF' } };
            if (colNumber > 2) cell.numFmt = '#,##0.0';
        }
    });

    // ─── TOP BORDER FOR JAMI ───────────────────────────────────────────────────
    const jamiRowNum = totalRow.number;
    for (let c = 1; c <= totalCols; c++) {
        const cell = ws.getCell(jamiRowNum, c);
        cell.border = {
            ...border(),
            top: { style: 'medium', color: { argb: BORDER_COLOR } },
        };
    }

    // ─── FREEZE PANES ──────────────────────────────────────────────────────────
    ws.views = [{ state: 'frozen', xSplit: 3, ySplit: 1 }];

    // ─── DOWNLOAD ──────────────────────────────────────────────────────────────
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
}
